import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  CardMedia,
  Dialog,
  Tooltip,
  Theme,
  useThemeProps,
  useTheme,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Hidden,
} from '@mui/material';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  applicationDownloadStatusCheck,
  checkIfApplicationIsNonIndividual,
  getBseClientActivationStatus,
  getFirstHolderName,
  maskInfo,
} from '../../utils/utilityFunctions';
import { ConfirmationDialog, LoadingDialog } from '../commonComponents';
import React, { useEffect, useState } from 'react';
import { getAllApplications, getApplicationDetails } from '../../redux-store/actions/application';
import { useDispatch, useSelector } from 'react-redux';
import {
  ApplicationProps,
  investmentDetailsAplicationRef,
} from '../../redux-store/types/api-types';
import { ColumnType, DataTable } from '../DataTable';
import {
  applicantStatusMasters,
  APPLICATION_LISTING_STATUS,
  BseClientActivationStatusMaster,
  COMMENT_ROLES,
  MPOWER_ACCOUNT_TYPE_MASTER,
  ONBOARDING_DOWNLOAD_APPLICATIONS_IGNORE_STATUS_CHECK,
  ONBOARDING_VIA_OPTIONS,
  PHYSICAL_DIGITAL_FLOW,
  POA_APPLICATION_STATUS,
  REJECTED_ROLES,
  USER_ROLES,
} from '../../utils/constant';
import { formatShortDateAndTime } from '../../utils/date';
import CloseIcon from '@mui/icons-material/Close';
import { ApplicationDownload, ApplicationRefs } from '../../redux-store/actions/onBoarding';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RootStateType } from '../../redux-store/reducers';
import { SxProps } from '@mui/system';
import { useSnackbar } from 'notistack';
import { GetAllApplicantionsResponseBody } from '../../redux-store/types/application';
import { reportDownload } from '../Reports/report-utils';
import { ActionIcon, InfoIconOutlined } from '../../assets/SvgIcons';
import dayjs from '../../utils/dayjs';
import { showError } from '../../redux-store/actions/auth';

const statusMap: { [key: string]: string } = {
  pending: 'BSE Client Activation Pending',
  active: 'BSE Client Activated',
  failed: 'BSE Client Activation Failed',
}

const getBseClientActivationStatusMsg = (value: string): string => {
  if (!['pending', 'active', 'failed'].includes(value)) {
    return value
  } else {
    return statusMap[value]
  }
}

export const getTableHeader = (): ColumnType[] => {
  return [
    {
      header: 'Application Number',
      label: 'applicationNumber',
      valueGetter: (row: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {row.applicationNumber || 'N/A'}
        </Typography>)
    },
    {
      header: 'Client Code',
      label: 'clientCode',
      valueGetter: (row: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {row?.clientCode ?? 'N/A'}
        </Typography>
      ),
    },
    {
      header: 'Investor Name',
      valueGetter: (row: ApplicationProps) =>
        row?.onboardingType?.toLowerCase() === 'minor'
          ? !(row?.applicants[0]?.minorFirstName) ? "N/A"
            :
            `${row?.applicants[0]?.minorFirstName ?? ""} ${row?.applicants[0]?.minorLastName ?? ""}`
          : maskInfo(getFirstHolderName(row?.applicants)) || 'N/A',
    },
    {
      header: 'Account Type',
      valueGetter: (row: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {MPOWER_ACCOUNT_TYPE_MASTER[row?.accountType] ?? 'N/A'}
        </Typography>
      )
    },
    {
      header: 'Mode of Holding / Status',
      renderCell: (row: ApplicationProps) => {
        return (
          <Typography
            variant='subtitle_500'
            sx={{ textTransform: 'capitalize' }}
          >
            {row.modeOfHolding || 'Single'} / {
              row?.applicants?.[0]?.status ?
                applicantStatusMasters[row.applicants[0].status] ?
                  applicantStatusMasters[row.applicants[0].status] : row.applicants[0].status
                : "N/A"
            }
          </Typography>
        );
      }
    },
    {
      header: 'Investor PAN',
      valueGetter: ({ applicants = [] }: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {maskInfo(applicants[0]?.panNo ?? '') || 'N/A'}
        </Typography>
      )
    },
    {
      header: 'Investor Email ID',
      valueGetter: ({ applicants = [] }: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {maskInfo(applicants[0]?.email ?? '') || 'N/A'}
        </Typography>
      )
    },
    {
      header: 'RM Name',
      valueGetter: (row: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {row?.rm?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      header: 'Onboarding Status',
      renderCell: (row: ApplicationProps) => {
        const { palette } = useTheme()
        const applicationStatus = APPLICATION_LISTING_STATUS[row.status];
        return (
          // <Typography
          //   className={applicationStatus}
          //   sx={{
          //     textDecoration: 'none',
          //     fontWeight: 500,
          //     fontSize: 14,
          //     color: '#B78813',
          //     '&.Draft': {
          //       color: 'rgba(51, 127, 201, 0.7)',
          //     },
          //     '&.Completed': {
          //       fontWeight: 600,
          //       color: 'rgba(35, 133, 63, 0.7)',
          //     },
          //     '&.Rejected': {
          //       color: '#BD2932 !important',
          //       fontWeight: 500,
          //     },
          //   }}
          //   component={RouterLink}
          //   to={
          //     checkIfApplicationIsNonIndividual(row)
          //       ? `/non-individual-application-details/${row.id}`
          //       : `/application-details/${row.id}`
          //   }>
          //   {applicationStatus}
          // </Typography>
          <Box
            className={APPLICATION_LISTING_STATUS[row.status]}
            sx={(theme: Theme) => ({
              '& .MuiTypography-root': {
                ...theme.typography.subtitle_500,
                color: '#B78813',
              },
              '&.Draft .MuiTypography-root': {
                color: palette.primary[500],
              },
              '&.Completed .MuiTypography-root': {
                color: palette.success[800],
              },
              '&.Rejected .MuiTypography-root': {
                color: palette.error[500],
              },
              '&.Sent .MuiTypography-root': {
                color: palette.warning[500],
              },
              '&.Invitation .MuiTypography-root': {
                color: palette.text.secondary + 'b2',
              },
              // backgroundColor: 'red'
            })}>
            <Typography
              component={RouterLink}
              to={
                checkIfApplicationIsNonIndividual(row)
                  ? `/non-individual-application-details/${row.id}`
                  : `/application-details/${row.id}`
              }
              sx={{ textDecoration: 'none' }}>
              {/* {row?.isBSEEnabled

                ?
                <>
                  {!getBseClientActivationStatus(row.bseClientActivationStatus || '') &&
                    `${APPLICATION_LISTING_STATUS[row.status]} ${row.applicationSentBack ? '(Sent Back)' : ''
                    }`}
                  {getBseClientActivationStatus(row.bseClientActivationStatus || '') && (
                    <Box
                      sx={{
                        fontWeight: 600,
                        color: 'rgba(51, 127, 201, 0.7)',
                      }}>
                      {BseClientActivationStatusMaster[row.bseClientActivationStatus || '']}
                    </Box>
                  )}
                </>

                : APPLICATION_LISTING_STATUS[row.status]
              } */}

              {(() => {
                const status = APPLICATION_LISTING_STATUS[row.status] ?? "-";
                const sentBackByRoleLabel = `${row?.sentBackByRole ?? ""}`.toLowerCase() === "amcapprover"
                  ? "By Checker"
                  : "By Compliance";
                const rejectedByRoleLabel = `${row?.rejectedByRole ?? ""}`.toLowerCase() === "amcapprover"
                  ? "By Checker"
                  : "By Compliance";

                const sentBackText = row.applicationSentBack
                  ? ` (Sent Back)${row?.sentBackByRole ? ` (${sentBackByRoleLabel})` : ''}`
                  : '';
                const rejectedText = (status === "Rejected" && row?.rejectedByRole)
                  ? ` (${rejectedByRoleLabel})`
                  : '';

                return `${status}${sentBackText}${rejectedText}`;
              })()}

              {/* {!getBseClientActivationStatus(row.bseClientActivationStatus || '') &&
                `${APPLICATION_LISTING_STATUS[row.status]} ${row.applicationSentBack ? '(Sent Back)' : ''
                }`}
              {getBseClientActivationStatus(row.bseClientActivationStatus || '') && (
                <Box
                  sx={{
                    fontWeight: 600,
                    color: 'rgba(51, 127, 201, 0.7)',
                  }}>
                  {BseClientActivationStatusMaster[row.bseClientActivationStatus || '']}
                </Box>
              )} */}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: 'BSE STAR STATUS',
      valueGetter: (row: ApplicationProps) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            variant='subtitle_500'
          >
            {row?.isBSEEnabled
              ? row?.bseClientActivationStatus
                ? getBseClientActivationStatusMsg(row?.bseClientActivationStatus)
                : 'N/A'
              : 'BSE Disabled'
            }
          </Typography>
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  margin: "0px !important",
                  backgroundColor: "transparent"
                }
              }
            }}
            title={
              <Box sx={{
                borderRadius: '10px',
                backgroundColor: '#0F131F',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '10px'  // Gap between each section
              }}>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    STATUS
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseStarServiceResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    CLIENT CREATION RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseStarResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    FATCA RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseFatcaResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    CHEQUE UPLOAD RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.cancelChequeUploadResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    AOF IMAGE RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseAofImageResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    MPOWER STATUS
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.mpowerResponse || '-'}
                  </Typography>
                </Box>
              </Box>
            }
          >
            <IconButton aria-label="BSE Star status details">
              <InfoIconOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
  ];
};

export default function DashboardAndApplicationsList({
  isLoading,
  applicationsList,
  boxStyles,
  DashboardApplicationsList,
  getApplication = () => { console.log('get Application') },
}: {
  isLoading: boolean;
  applicationsList: ApplicationProps[];
  boxStyles?: SxProps<Theme>;
  DashboardApplicationsList?: boolean;
  getApplication?: () => void
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();

  const [rejectionDialog, setRejectionDialog] = useState(false);

  const [rejectedApplication, setRejectedApplication] = useState<ApplicationProps>();
  const [isDialogLoading, setDialogLoading] = useState(false);
  const [id, setId] = useState('');
  const [checkPoaOrNI, setCheckPoaOrNI] = useState(false);
  const [tooltip, setTooltip] = useState('Click to Copy');
  const [applicationRefsList, setApplicationRefsList] = useState<investmentDetailsAplicationRef[]>(
    []
  );
  const [open, setOpen] = useState(false);
  const [deletePopUp, setDeletePopUp] = useState<{ id: string } | null>(null);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [applicationsListData, setApplicationsListData] = useState<ApplicationProps[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // console.log('deletePopUp', deletePopUp)

  const handleClose = () => {
    setOpen(false), setId('');
  };
  const handleDialogClose = () => {
    setDialogLoading(false);
    setOpen(true);
  };

  const handleRejectionDialogClose = () => {
    setRejectionDialog(false);
  };
  useEffect(() => {
    setApplicationsListData(applicationsList);
  }, [applicationsList]);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        if (id) {
          const responseApplicationRefsList = (await dispatch(
            ApplicationRefs(id)
          )) as unknown as investmentDetailsAplicationRef[];
          if (!componentIsAlive) {
            return;
          }
          setApplicationRefsList(responseApplicationRefsList);
          handleDialogClose();
        }
      } catch (e) {
        setDialogLoading(false);
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [id]);

  const additionalCell = {
    header: 'Action',
    renderEditDeleteCell: (row: ApplicationProps): JSX.Element => {
      const [loadingPopup, setLoadingPopup] = useState(false);
      const { palette } = useTheme()
      const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
      const [selectedIndex, setSelectedIndex] = React.useState(1);
      const open = Boolean(anchorEl);

      const options = [
        ...(row.editable ? [{
          label: 'Edit',
          name: 'edit',
        }] : []),
        {
          label: 'Download',
          name: 'download',
          disable: ONBOARDING_DOWNLOAD_APPLICATIONS_IGNORE_STATUS_CHECK ?
            false :
            row?.status?.toLowerCase() === 'completed' ? false : true
        },
        ...(row.deletable ? [{
          label: 'Delete',
          name: 'delete',
        }] : []),
        !['draft', 'rejected', 'sent_to_poa_approver', 'sent_to_amc_approver'].includes(row.status) && row?.isEsignEnabled && {
          label: 'Link',
          name: 'link',
          disable: false
        },
        APPLICATION_LISTING_STATUS[row.status] === APPLICATION_LISTING_STATUS.rejected && {
          label: 'Info',
          name: 'info',
          disable: false
        }
      ];

      const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        // console.log(row, row.deletable, row.editable);
      };

      const handleMenuItemClick = (
        event: React.MouseEvent<HTMLElement>,
        index: number,
        option: {
          label: string;
          name: string;
        }
      ) => {
        // console.log('option', option);
        if (option.name === 'edit') editHandler()
        else if (option.name === 'delete') deleteHandler()
        else if (option.name === 'download') downloadHandler()
        else if (option.name === 'link') linkHandler()
        else if (option.name === 'info') infoHandler()
        setSelectedIndex(index);
        setAnchorEl(null);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const editHandler = () => {
        row.editable &&
          history.push(
            checkIfApplicationIsNonIndividual(row)
              ? `/non-individual-application`
              : `/application`,
            { id: row.id }
          );
      }

      const deleteHandler = () => {
        // console.log('row', row);
        row.deletable && setDeletePopUp({ id: row.id })
      }

      const downloadHandler = async () => {
        try {
          // Below if condition checks if application status is NOT completed 
          if (applicationDownloadStatusCheck(row.status || '')) {
            setLoadingPopup(true);
            const res = (await dispatch(ApplicationDownload(row.id))) as unknown as any;
            reportDownload(res as unknown as Blob, `${row.id}.pdf`);
            setLoadingPopup(false);
            enqueueSnackbar('Downloaded successfully', {
              variant: 'success',
              autoHideDuration: 3000,
            });
            return;
          }

          if (
            PHYSICAL_DIGITAL_FLOW &&
            ONBOARDING_VIA_OPTIONS[(row?.onboardingVia ?? "")?.toLowerCase()] === ONBOARDING_VIA_OPTIONS['physical']
          ) {
            setLoadingPopup(true);
            const res = (await dispatch(ApplicationDownload(row.id))) as unknown as any;
            reportDownload(res as unknown as Blob, `${row.id}.pdf`);
            setLoadingPopup(false);
            enqueueSnackbar('Downloaded successfully', {
              variant: 'success',
              autoHideDuration: 3000,
            });
            return;
          }

          if (
            PHYSICAL_DIGITAL_FLOW &&
            ONBOARDING_VIA_OPTIONS[(row?.onboardingVia ?? "")?.toLowerCase()] === ONBOARDING_VIA_OPTIONS['digital']
          ) {
            if (row?.signedLeegalityFile?.url) {
              window.open(row?.signedLeegalityFile?.url, '_self');
              enqueueSnackbar('Downloaded successfully', {
                variant: 'success',
                autoHideDuration: 3000,
              });
            } else {
              dispatch(showError("File not found. Download failed."))
            }
            return
          }
        } catch (e) {
          setLoadingPopup(false);
          console.error((e as Error).message);
        }
      }

      const linkHandler = () => {
        setId(row.id as string),
          setDialogLoading(true),
          setCheckPoaOrNI(row.hasPOA || checkIfApplicationIsNonIndividual(row));
      }

      const infoHandler = () => {
        setRejectedApplication(row);
        setRejectionDialog(true);
      }

      const handleLoadingPopupClose = () => {
        setLoadingPopup(false);
      };

      return (
        <>
          <Hidden only='xs'>
            <div>
              <List
                component="nav"
                aria-label="Device settings"
                sx={{ padding: 0 }}
              >
                <ListItemButton
                  id="lock-button"
                  aria-haspopup="listbox"
                  aria-controls="lock-menu"
                  aria-label="when device is locked"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClickListItem}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <ActionIcon />
                </ListItemButton>
              </List>
              <Menu
                id="lock-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'lock-button',
                  role: 'listbox',
                  style: {
                    padding: '8px',
                    borderRadius: '16px'
                  }
                }}

              >
                {[...options?.filter((option) => option)].map((option: any, index: any) => (
                  <MenuItem
                    key={index}
                    disabled={option?.disable}
                    // selected={index === selectedIndex}
                    onClick={(event) => handleMenuItemClick(event, index, option)}
                    sx={{
                      '&:focus': {
                        backgroundColor: palette.primary[50],
                        color: palette.primary[500],
                        borderRadius: '8px'
                      },
                    }}
                  >
                    {option?.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>

          </Hidden>
          <Hidden smUp={true}>
            <Box display="flex">
              {APPLICATION_LISTING_STATUS[row.status] === APPLICATION_LISTING_STATUS.rejected && (
                <IconButton>
                  <CardMedia
                    component="img"
                    src="/images/comment.svg"
                    alt="comment"
                    sx={{ width: '20px' }}
                    onClick={() => {
                      setRejectedApplication(row);
                      setRejectionDialog(true);
                    }}
                  />
                </IconButton>
              )}
              {!['draft', 'rejected', 'sent_to_poa_approver', 'sent_to_amc_approver'].includes(
                row.status
              ) && (
                  <Tooltip title="Link">
                    <IconButton
                      sx={{
                        p: 0,
                        ':hover': {
                          bgcolor: '#F4FCFC',
                        },
                      }}
                      onClick={() => {
                        setId(row.id as string),
                          setDialogLoading(true),
                          setCheckPoaOrNI(row.hasPOA || checkIfApplicationIsNonIndividual(row));
                      }}>
                      <CardMedia
                        component="img"
                        src="/images/link-icon.svg"
                        alt="Link"
                        sx={{ width: '30px' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title={row.editable ? 'Edit' : ''}>
                <IconButton
                  onClick={() => {
                    row.editable &&
                      history.push(
                        checkIfApplicationIsNonIndividual(row)
                          ? `/non-individual-application`
                          : `/application`,
                        { id: row.id }
                      );
                  }}
                  sx={{
                    ':hover': {
                      bgcolor: '#F4FCFC',
                    },
                    '&.Mui-disabled': {
                      cursor: row.editable ? 'pointer' : 'not-allowed !important',
                      pointerEvents: 'all',
                    },
                  }}
                  disabled={!row.editable}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {[
                USER_ROLES.RM,
                USER_ROLES.DISTRIBUTOR,
                USER_ROLES.SUBDISTRIBUTOR,
                USER_ROLES.OPE,
                USER_ROLES.OPM,
                USER_ROLES.ONBOARDING_SPECIALIST,
                USER_ROLES.IA
              ].includes(role) && (
                  <Tooltip title={row.deletable ? 'Delete' : 'Application can`t be Deleted'}>
                    <IconButton
                      sx={{
                        p: 0,
                        pr: 0.5,
                        ':hover': {
                          bgcolor: '#F4FCFC',
                        },
                        '&.Mui-disabled': {
                          cursor: row.deletable ? 'pointer' : 'not-allowed !important',
                          pointerEvents: 'all',
                        },
                      }}
                      onClick={() => row.deletable && setDeletePopUp({ id: row.id })}
                      disabled={!row.deletable}>
                      <DeleteOutlineIcon color={row.deletable ? 'error' : 'disabled'} fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip
                title={
                  applicationDownloadStatusCheck(row.status || '') || row?.signedLeegalityFile?.url
                    ? 'Download'
                    : 'Only Signed Documents can be Downloaded'
                }>
                <IconButton
                  sx={{
                    p: 0,
                    pr: 0.5,
                    ':hover': {
                      bgcolor: '#F4FCFC',
                    },
                    cursor: 'pointer',
                    pointerEvents: 'all',
                  }}
                  onClick={async () => {
                    try {
                      if (applicationDownloadStatusCheck(row.status || '')) {
                        setLoadingPopup(true);
                        const res = (await dispatch(ApplicationDownload(row.id))) as unknown as any;
                        reportDownload(res as unknown as Blob, `${row.id}.pdf`);
                        setLoadingPopup(false);
                        return;
                      }
                      row?.signedLeegalityFile?.url && window.open(row?.signedLeegalityFile?.url);
                    } catch (e) {
                      setLoadingPopup(false);
                      console.error((e as Error).message);
                    }
                  }}>
                  <CardMedia
                    component="img"
                    src={
                      applicationDownloadStatusCheck(row.status || '') || row?.signedLeegalityFile?.url
                        ? '/images/download-icon.png'
                        : '/images/download-icon-disabled.png'
                    }
                    alt="Download"
                  //sx={{ width: '30px' }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Hidden>
          <LoadingDialog
            loadingPopup={loadingPopup}
            onLoadingPopupClose={handleLoadingPopupClose}
            loaderText={'Downloading'}
          />
        </>

      );
    },
  };

  const { palette } = useTheme()

  return (
    <>
      <DataTable
        isLoading={isLoading}
        tableHeader={[...getTableHeader(), additionalCell]}
        tableData={applicationsListData}
        rowCustomStyles={{
          '&:last-child td, &:last-child th': { border: 0 },
          '.MuiTableCell-body ': { color: palette.text.primary, fontWeight: 500 },
          // background: 'transparent',
        }}
        boxStyles={boxStyles}
      />

      <Dialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={4}
          pb={2}>
          <Typography sx={{ color: 'primary.main', fontSize: '20px', fontWeight: 500 }}>
            Get Link
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Grid>
        {applicationRefsList &&
          applicationRefsList.map((ref) => {
            return (
              <React.Fragment key={ref.id}>
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  px={4}
                  py={1}
                  sx={{
                    ':hover': {
                      bgcolor: 'customColors.darkGrey',
                    },
                    ':last-of-type': {
                      mb: 5,
                    },
                  }}>
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '18px',
                      fontWeight: 500,
                    }}>
                    Copy Link For {ref.name}
                  </Typography>
                  <Tooltip title={tooltip}>
                    <IconButton
                      onMouseOver={() => {
                        setTooltip('Copy Link');
                      }}
                      onClick={() => {
                        setTooltip('Copied'),
                          navigator.clipboard.writeText(
                            (checkPoaOrNI || ref.invitationUrl?.length > 0)
                              ? ref.invitationUrl
                              : `${window.location.origin}/investment-details/${ref.referenceId}/investor-login`
                          );
                      }}>
                      <CardMedia
                        component="img"
                        src="/images/link-icon.svg"
                        alt="Link"
                        sx={{ width: '35px' }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </React.Fragment>
            );
          })}
      </Dialog>
      <Dialog
        onClose={handleDialogClose}
        open={isDialogLoading}
        sx={{
          '.MuiPaper-root ': {
            p: 4,
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
            },
          }}>
          <Typography sx={{ color: 'primary.main', lineHeight: '35px', textAlign: 'center' }}>
            Loading...
          </Typography>
        </Box>
      </Dialog>
      <Dialog
        onClose={handleRejectionDialogClose}
        open={rejectionDialog}
        sx={{
          '.MuiPaper-root ': {
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <IconButton
          onClick={handleRejectionDialogClose}
          sx={{ alignSelf: 'flex-end', mt: 2, mr: 2 }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        {rejectedApplication && (
          <Box
            sx={{
              width: { xs: '80%', sm: '80%' },
              mx: 'auto',
              '.MuiTypography-root': {
                fontWeight: 500,
              },
              mt: 1,
              mb: 5,
            }}>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 6.7 },
                mt: 1,
              }}>
              Application Id:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {rejectedApplication.applicationNumber}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 1.5 },
                mt: 1,
              }}>
              Application Status:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#BD2932',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {POA_APPLICATION_STATUS[rejectedApplication.status]}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 9 },
                mt: 1,
              }}>
              Rejected on:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {/* {formatShortDateAndTime(rejectedApplication.rejectedAt)} */}
                {dayjs(rejectedApplication?.rejectedAt).format('DD MMM YY h:mm A')}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 9.5 },
                mt: 1,
              }}>
              Rejected by:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {/* {REJECTED_ROLES[rejectedApplication.rejectedByRole]} */}
                {rejectedApplication?.rejectedBy}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                mt: 1,
              }}>
              Reason for rejection:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {rejectedApplication.rejectedReason}
              </Typography>
            </Typography>
            <Button
              variant="contained"
              sx={{ ml: '32%', lineHeight: 1.1, width: '30%', mt: 3 }}
              onClick={handleRejectionDialogClose}>
              Done
            </Button>
          </Box>
        )}
      </Dialog>
      <ConfirmationDialog
        message={'Are you sure you want to delete Application ?'}
        open={deletePopUp !== null}
        setOpen={() => setDeletePopUp(null)}
        onSave={async () => {
          try {
            setDeletePopUp(null);
            const _application = (await dispatch(
              getApplicationDetails(deletePopUp?.id || '', 'delete')
            )) as unknown as { code: string; message: string };
            if (_application.code === 'SUCCESS') {
              getApplication()
            } else {
              enqueueSnackbar('Something wrong', {
                variant: 'error',
                autoHideDuration: 3000,
              });
            }
            // if (DashboardApplicationsList) {
            //   setDialogLoading(true);
            //   const response = (await dispatch(
            //     getAllApplications({
            //       page: 1,
            //       limit: 4,
            //     })
            //   )) as unknown as GetAllApplicantionsResponseBody;
            //   const { applications } = response || {};
            //   setApplicationsListData(applications);
            //   setDialogLoading(false);
            // } else {
            //   setApplicationsListData(
            //     applicationsListData.filter(
            //       (application) => application.id !== deletePopUp?.id || ''
            //     )
            //   );
            // }
            enqueueSnackbar('Application Deleted Successfully', {
              variant: 'success',
              autoHideDuration: 3000,
            });
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDeletePopUp(null)}
      />
    </>
  );
}
